import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnChanges,
  output,
  Signal,
  signal,
  SimpleChanges
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TaskDescriptionDraftService } from '@app/components/task/task-splitview-list/task-details-content/task-description-draft.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiTiptapEditorComponent } from '@app/shared/ui/tiptap-editor/ui-tiptap-editor.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-task-details-description',
  templateUrl: './task-details-description.component.html',
  imports: [MatIconModule, TranslatePipe, UiTiptapEditorComponent, NgClass]
})
export class TaskDetailsDescriptionComponent implements OnChanges {
  private readonly deviceService = inject(DeviceService);
  private readonly draftState = inject(TaskDescriptionDraftService);

  private readonly originalDescription = signal('');
  private readonly isEditing = signal(false);
  /** Description last sent for saving, used to recognise the task update that confirms it. */
  private submittedDescription: string | null = null;

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;

  protected readonly currentDescription = signal('');

  protected readonly contentChanged = computed((): boolean => this.originalDescription() !== this.currentDescription());
  protected readonly isDescriptionEditing = computed(() => this.createMode() || this.isEditing());

  public task = input<Task | null>();
  public createMode = input<boolean>(false);
  public minHeight = input<string>('150px');

  public descriptionChange = output<string>();
  public unsavedChangesChange = output<boolean>();

  public constructor() {
    this.initEditorContent();

    effect(() => {
      this.unsavedChangesChange.emit(this.contentChanged());
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const taskChange = changes['task'];
    if (!taskChange) {
      return;
    }

    const previousTaskId = (taskChange.previousValue as Task | null | undefined)?.id;
    const sameTask = !!previousTaskId && previousTaskId === this.task()?.id;
    if (sameTask && this.contentChanged() && !this.isSavedDraftConfirmed()) {
      // Another field of the same task was saved (or a file uploaded) while the description is
      // being edited: keep the draft and only move the saved baseline to the server's version.
      this.originalDescription.set(this.task()?.description ?? '');
      return;
    }

    const taskId = this.task()?.id;
    if (sameTask && this.isSavedDraftConfirmed() && taskId) {
      this.draftState.clear(taskId);
    }

    this.submittedDescription = null;
    this.isEditing.set(false);
    this.initEditorContent();
    if (taskChange.firstChange && taskId) {
      const draft = this.draftState.get(taskId);
      if (draft !== null && draft !== this.originalDescription()) {
        this.currentDescription.set(draft);
        this.isEditing.set(true);
      }
    }
  }

  protected startDescriptionEdit(): void {
    if (this.createMode() || this.isEditing()) {
      return;
    }
    this.isEditing.set(true);
  }

  protected onDescriptionEdited(description: string | null): void {
    const value = description ?? '';
    this.currentDescription.set(value);

    if (this.createMode()) {
      this.descriptionChange.emit(value);
    } else {
      const taskId = this.task()?.id;
      if (!taskId) {
        return;
      }
      if (value === this.originalDescription()) {
        this.draftState.clear(taskId);
      } else {
        this.draftState.set(taskId, value);
      }
    }
  }

  protected revertDesc(): void {
    this.currentDescription.set(this.originalDescription());
    this.isEditing.set(false);
    const taskId = this.task()?.id;
    if (taskId) {
      this.draftState.clear(taskId);
    }
  }

  protected updateDescription(): void {
    const task = this.task();
    if (!task?.id) {
      return;
    }

    this.submittedDescription = this.currentDescription();
    this.descriptionChange.emit(this.currentDescription());
    this.isEditing.set(false);
  }

  /**
   * The update carries the description the user just saved: the stored description changed and the
   * editor still holds exactly the submitted text. The server's version (possibly normalized HTML)
   * then replaces the draft. A cancelled save or further typing keeps the draft.
   */
  private isSavedDraftConfirmed(): boolean {
    const serverDescription = this.task()?.description ?? '';
    return (
      this.submittedDescription !== null &&
      this.submittedDescription === this.currentDescription() &&
      serverDescription !== this.originalDescription()
    );
  }

  private initEditorContent(): void {
    const description = this.task()?.description ?? '';
    this.originalDescription.set(description);
    this.currentDescription.set(description);
  }
}
